'use client';
import { BreadcrumbsAndTitle } from '@c/BreadcrumbsAndTitle';
import Accordion from '@ui/Accordion';
import Button from '@ui/Button';
import Select from '@ui/Select';
import { FAQTopic, FAQ_TOPIC_QUESTION_MAP } from '@util/maps/faq';
import { getFaqUrl } from '@util/urlHelpers';
import { injectFAQScript } from 'features/schemaMarkups';
import { useEffect, useState } from 'react';
const llorem =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.';

const getFaqs = (topic: FAQTopic) => {
  return FAQ_TOPIC_QUESTION_MAP[topic];
};

const getTopics = () => {
  return Object.keys(FAQ_TOPIC_QUESTION_MAP) as FAQTopic[];
};

interface FAQProps {
  topic?: FAQTopic;
  q?: string;
}
const FAQPage = ({ topic, q }: FAQProps) => {
  const [currentTopic, setCurrentTopic] = useState<FAQTopic>(
    topic ?? 'Account'
  );
  const [currentQuestion, setCurrentQuestion] = useState<string | undefined>(q);
  useEffect(() => {
    const url = getFaqUrl(currentTopic, currentQuestion);
    const baseUrl = window.location.origin;
    const newUrl = baseUrl + url;
    if (window.location.href !== newUrl) {
      window.history.pushState({}, '', newUrl);
    }
  }, [currentTopic, currentQuestion]);

  const topics = getTopics();
  const faqs = getFaqs(currentTopic);
  const handleTopicChange = (topic: FAQTopic) => {
    setCurrentQuestion(undefined);
    setCurrentTopic(topic);
  };

  return (
    <div className="mb-[6rem] flex w-full max-w-[174rem] flex-col px-[1.6rem] sm:px-default-padding-x">
      <script
        id="schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: injectFAQScript(FAQ_TOPIC_QUESTION_MAP[currentTopic]) ?? '',
        }}
      />
      <BreadcrumbsAndTitle
        items={[
          { label: 'Help Center', href: '/help' },
          {
            label: currentTopic,
            href: '#',
            current: true,
            currentCss: 'text-brand-black',
          },
        ]}
        title="Frequently Asked Questions"
      />
      {/* main content container */}
      <div className="mt-[4rem] flex w-full flex-col gap-[3rem] sm:flex-row">
        {/* left nav */}
        <div className="w-full sm:w-[35rem] sm:shrink-0">
          <div className="block w-full sm:hidden">
            <Select
              allowEmpty={false}
              value={currentTopic}
              onChange={(value) => handleTopicChange(value.value as FAQTopic)}
              options={topics.map((t) => ({
                label: t,
                value: t,
                id: t,
              }))}
            />
          </div>
          <div className="hidden sm:block">
            <h5 className="text-[2.25rem] font-bold text-brand-black">Topics</h5>
            <div className="mt-[2.4rem] flex flex-col items-start gap-[0.8rem]">
              {topics.map((topic, i) => {
                const isActive = topic === currentTopic;
                return (
                  <button
                    key={topic + i}
                    className={`flex w-full items-center py-[1rem] pl-[1.5rem] text-left text-[1.75rem] font-medium transition-colors ${
                      isActive
                        ? 'border-l-2 border-brand-secondary text-brand-secondary'
                        : 'text-brand-black hover:text-brand-secondary'
                    }`}
                    onClick={() => handleTopicChange(topic)}
                  >
                    {topic}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* right*/}
        <div className="mt-[3.6rem] flex w-full flex-col gap-[8rem] sm:mt-0">
          {/* FAQs */}
          <div className="flex flex-col">
            {faqs.map((faq, i) => {
              return (
                <Accordion
                  key={`${currentTopic}${faq.q}${i}`}
                  title={faq.q}
                  titleClassName="text-[1.75rem]"
                  divider={true}
                  hover="text"
                  intent="faq"
                  highlightHeaderOnOpen={true}
                  startOpen={currentQuestion === faq.q}
                  onOpen={() => setCurrentQuestion(faq.q)}
                  onClose={() => setCurrentQuestion(undefined)}
                >
                  <div className="pb-[1.6rem] text-[1.75rem] font-medium text-[#444444]">
                    {faq.a || llorem}
                  </div>
                </Accordion>
              );
            })}
          </div>

          {/* banner */}
          <div className="relative flex min-h-[30rem] w-full flex-col items-center justify-center gap-[3rem] overflow-clip rounded-[2rem] bg-brand-black bg-faq-banner bg-cover bg-center px-[2rem] py-[3rem]">
            <div className="absolute inset-0 bg-black/50" />
            <div className="relative z-10 flex flex-col items-center gap-[1rem] text-center text-brand-white">
              <h3 className="select-none text-[2.6rem] font-bold leading-[1.3] tracking-[-0.3px] sm:text-[3.75rem]">
                Didn&apos;t find an answer to your question?
              </h3>
              <p className="select-none text-[1.75rem] font-medium leading-[1.5]">
                Get in contact with us!
              </p>
            </div>
            <div className="relative z-10">
              <Button text="Contact MX Locker" href="/contact" type="secondary" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
